import {
  convertLocalDateTimeToUtcDateTime,
  convertUtcDateTimeToLocalDateTime,
  getMinutesFromDateTime,
} from 'framework/utils';
import {
  TIMELINE_START_MINUTES,
  STEP_MINUTES,
  DEFAULT_SLOT_MINUTES,
  MIN_SLOT_MINUTES,
  TIMELINE_END_MINUTES,
  FOCUS_TIME_LIMIT_ERROR_TEXT,
} from '../constants';
import { FocusTimeSlotsPayload, FocusTimeSlot } from '../types';
import {
  isValidSlot,
  getSlotEndMinutes,
  overlapsAny,
  getSlotStartMinutes,
  createFocusSlot,
  createSlotId,
  isLocalFocusSlotId,
} from './time';

export const normalizeSlotsPayload = (payload: FocusTimeSlotsPayload): FocusTimeSlot[] => {
  const payloadSlots = payload;
  if (!Array.isArray(payloadSlots)) {
    return [];
  }

  const currentMinutes = getMinutesFromDateTime();
  const normalizedSlots = payloadSlots.reduce<FocusTimeSlot[]>((acc, slot) => {
    if (!slot.startAt || !slot.endAt) {
      return acc;
    }

    const normalizedSlot = {
      id: slot.id || createSlotId(),
      startAt: convertUtcDateTimeToLocalDateTime(slot.startAt),
      endAt: convertUtcDateTimeToLocalDateTime(slot.endAt),
    };

    if (isValidSlot(normalizedSlot) && getSlotEndMinutes(normalizedSlot) > currentMinutes) {
      acc.push(normalizedSlot);
    }

    return acc;
  }, []);

  return normalizedSlots.reduce<FocusTimeSlot[]>((result, slot) => {
    if (!overlapsAny(slot, result)) {
      result.push(slot);
    }

    return result;
  }, []);
};

export const toFocusSchedulesRequestPayload = (slots: FocusTimeSlot[]) =>
  slots.map(slot => ({
    ...(isLocalFocusSlotId(slot.id) ? {} : { id: slot.id }),
    startAt: convertLocalDateTimeToUtcDateTime(slot.startAt),
    endAt: convertLocalDateTimeToUtcDateTime(slot.endAt),
  }));

export const areSlotsEqual = (left: FocusTimeSlot[], right: FocusTimeSlot[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((slot, index) => {
    const comparedSlot = right[index];
    return slot.id === comparedSlot.id && slot.startAt === comparedSlot.startAt && slot.endAt === comparedSlot.endAt;
  });
};

export const isSelectableSlot = (slot: FocusTimeSlot, minSelectableMinutes: number) => {
  return getSlotStartMinutes(slot) >= minSelectableMinutes && isValidSlot(slot);
};

export const isSelectableSlotEnd = (slot: FocusTimeSlot, minSelectableMinutes: number) => {
  return getSlotEndMinutes(slot) >= minSelectableMinutes && isValidSlot(slot);
};

export const getAvailableSlot = (
  requestedStartMinutes: number,
  slots: FocusTimeSlot[],
): { startMinutes: number; endMinutes: number } | null => {
  const sortedSlots = [...slots].sort((a, b) => getSlotStartMinutes(a) - getSlotStartMinutes(b));

  let previousEndMinutes = TIMELINE_START_MINUTES;
  for (const slot of sortedSlots) {
    const slotStartMinutes = getSlotStartMinutes(slot);
    const slotEndMinutes = getSlotEndMinutes(slot);
    const gapStartMinutes =
      previousEndMinutes === TIMELINE_START_MINUTES ? TIMELINE_START_MINUTES : previousEndMinutes + STEP_MINUTES;
    const gapEndMinutes = slotStartMinutes - STEP_MINUTES;

    if (requestedStartMinutes <= gapEndMinutes) {
      const startMinutes = Math.max(requestedStartMinutes, gapStartMinutes);
      const endMinutes = Math.min(startMinutes + DEFAULT_SLOT_MINUTES, gapEndMinutes);

      return endMinutes - startMinutes >= MIN_SLOT_MINUTES ? { startMinutes, endMinutes } : null;
    }

    if (requestedStartMinutes < slotStartMinutes) {
      return null;
    }

    if (requestedStartMinutes >= slotStartMinutes && requestedStartMinutes < slotEndMinutes) {
      return null;
    }

    previousEndMinutes = Math.max(previousEndMinutes, slotEndMinutes);
  }

  const gapStartMinutes =
    previousEndMinutes === TIMELINE_START_MINUTES ? TIMELINE_START_MINUTES : previousEndMinutes + STEP_MINUTES;
  const startMinutes = Math.max(requestedStartMinutes, gapStartMinutes);
  const endMinutes = Math.min(startMinutes + DEFAULT_SLOT_MINUTES, TIMELINE_END_MINUTES);

  return endMinutes - startMinutes >= MIN_SLOT_MINUTES ? { startMinutes, endMinutes } : null;
};

export const createSelectableDefaultSlot = (minSelectableMinutes: number, slots: FocusTimeSlot[]) => {
  const availableSlot = getAvailableSlot(minSelectableMinutes, slots);
  if (!availableSlot) {
    return null;
  }

  const defaultSlot = createFocusSlot(createSlotId(), availableSlot.startMinutes, availableSlot.endMinutes);

  return isSelectableSlot(defaultSlot, minSelectableMinutes) ? defaultSlot : null;
};

export const createFocusTimeSlotFromMinutes = (id: string, startMinutes: number, endMinutes: number) => {
  return createFocusSlot(id, startMinutes, endMinutes);
};

export const updateFocusTimeSlotMinutes = (slot: FocusTimeSlot, startMinutes: number, endMinutes: number) => {
  return createFocusSlot(slot.id, startMinutes, endMinutes, slot.startAt);
};

export const isFocusTimeLimitError = (reason: unknown) => {
  const message = String(reason);

  return message.includes('HTTP error 400') && message.includes(FOCUS_TIME_LIMIT_ERROR_TEXT);
};

export const isCurrentTimeWithinSlot = (slot: FocusTimeSlot, currentMinutes = getMinutesFromDateTime()) => {
  return getSlotStartMinutes(slot) <= currentMinutes && currentMinutes < getSlotEndMinutes(slot);
};
